import React from "react";
import UserAvatar from "../../../components/ui/Avatar";
import { useNavigate } from "react-router-dom";
import { Camera, Video, ChevronRight } from "lucide-react";

import {
  CAPTURE_MODE_LABEL,
  SESSION_STATUS_STYLE,
  employeeName,
  formatBytes,
  formatTime,
  formatWorked,
} from "../utils/format";

const StatusBadge = ({ status }) => {
  const style = SESSION_STATUS_STYLE[status] ?? SESSION_STATUS_STYLE.ended;
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${style.bg} ${style.color}`}
    >
      {style.label}
    </span>
  );
};

const MediaCell = ({ session }) => (
  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-light-500">
    {session.chunkCount > 0 && (
      <span className="inline-flex items-center gap-1" title="video chunks">
        <Video className="w-3.5 h-3.5" />
        {session.chunkCount}
      </span>
    )}
    {session.screenshotCount > 0 && (
      <span className="inline-flex items-center gap-1" title="screenshots">
        <Camera className="w-3.5 h-3.5" />
        {session.screenshotCount}
      </span>
    )}
    {!session.chunkCount && !session.screenshotCount && <span>—</span>}
  </div>
);

const Avatar = ({ employee }) => <UserAvatar src={employee?.photo} name={employee?.fullNameEn || employee?.nickname} size="sm" />;

/**
 * Hand-rolled rather than using components/tables/DataTable, which is
 * onEdit/onDelete-shaped and has no row click or pagination. Mirrors
 * AdminAttendance: mobile cards under md, a real table above it.
 */
const SessionsTable = ({ records }) => {
  const navigate = useNavigate();
  const open = (id) => navigate(`/management/monitoring/sessions/${id}`);

  const totalBytes = (s) =>
    (s.recordedBytes || 0) + (s.screenshotBytes || 0);

  return (
    <>
      {/* ── Mobile ── */}
      <div className="block md:hidden divide-y divide-gray-100 dark:divide-dark-100">
        {records.map((s) => (
          <button
            key={s._id}
            onClick={() => open(s._id)}
            className="w-full p-4 flex items-start gap-3 text-left hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
          >
            <Avatar employee={s.employeeId} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                  {employeeName(s.employeeId)}
                </p>
                <StatusBadge status={s.status} />
              </div>
              <p className="text-xs text-gray-500 dark:text-light-500 mt-0.5">
                {s.date} · {formatTime(s.startedAt)} –{" "}
                {s.endedAt ? formatTime(s.endedAt) : "now"}
              </p>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
                  {formatWorked(s.workedSeconds)}
                </span>
                <MediaCell session={s} />
                <span className="text-xs text-gray-400 dark:text-light-500">
                  {formatBytes(totalBytes(s))}
                </span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 dark:text-dark-200 mt-1 flex-shrink-0" />
          </button>
        ))}
      </div>

      {/* ── Desktop ── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-dark-300/50">
            <tr className="text-left text-xs text-gray-500 dark:text-light-500">
              <th className="px-6 py-3 font-medium">Employee</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Started</th>
              <th className="px-4 py-3 font-medium">Ended</th>
              <th className="px-4 py-3 font-medium">Worked</th>
              <th className="px-4 py-3 font-medium">Paused</th>
              <th className="px-4 py-3 font-medium">Capture</th>
              <th className="px-4 py-3 font-medium">Media</th>
              <th className="px-4 py-3 font-medium">Size</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-100">
            {records.map((s) => (
              <tr
                key={s._id}
                onClick={() => open(s._id)}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
              >
                <td className="px-6 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar employee={s.employeeId} />
                    <span className="text-sm font-medium text-gray-900 dark:text-light-100">
                      {employeeName(s.employeeId)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-light-400 tabular-nums">
                  {s.date}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-light-400 tabular-nums">
                  {formatTime(s.startedAt)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-light-400 tabular-nums">
                  {s.endedAt ? formatTime(s.endedAt) : "—"}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-light-100 tabular-nums">
                  {formatWorked(s.workedSeconds)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-light-500 tabular-nums">
                  {s.pausedSeconds ? formatWorked(s.pausedSeconds) : "—"}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-light-500">
                  {CAPTURE_MODE_LABEL[s.captureMode] ?? "—"}
                  {/* An override is why this session may differ from the global
                      default, so surface it rather than leaving it a mystery. */}
                  {s.overrideId && (
                    <span
                      className="ml-1 text-primary-accent"
                      title="A per-employee override applied to this session"
                    >
                      ●
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <MediaCell session={s} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-light-500 tabular-nums">
                  {formatBytes(totalBytes(s))}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={s.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default SessionsTable;
